import React from 'react';
import type { TraceResult, TraceSnapshot } from '../../services/tracking.service';

/**
 * gwiTrace shipment popup — a React port of v1's `gwitraceShipmentHtml`
 * (cms libfast_cms.pl): a Shipment ID / Last Synced header, an Exceptions
 * block, then two-column Shipment / Ocean (SSLine) / Rail sections, provider
 * health, and a collapsible Raw Results JSON dump. Datetimes render in the
 * port's own local time — the same wall clock gwi360 writes to the CMS cell
 * (field_mapper._fmt_datetime) and the driver's EIR shows; only UTC values
 * fall back to Central. Date-only fields show as calendar dates.
 */

const dtf = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Chicago',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hour12: false,
});

export const fmtDate = (v: unknown): string => {
  if (v == null || v === '') return '';
  const s = String(v);
  // Date-only → calendar date, no time.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-');
    return `${m}/${d}/${y}`;
  }
  // Wall clock as written when the value is naive (last_synced_at, which the
  // DB stores without an offset) or carries a non-zero offset: gwiTrace
  // localizes each time to the port's zone, so ZCSU7051550's out-gate
  // '13:16:29-04:00' reads 13:16 at Savannah — the CMS cell and the EIR, not
  // a Central 12:16 an hour off from both.
  const isUtc = /[Zz]$/.test(s) || /[+-]00:?00$/.test(s);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (m && !isUtc) {
    return `${m[2]}/${m[3]}/${m[1]} ${m[4]}:${m[5]}`;
  }
  // UTC (older snapshots, system stamps) → Central, as _fmt_datetime does.
  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return s;
  const p = Object.fromEntries(dtf.formatToParts(dt).map((x) => [x.type, x.value]));
  return `${p.month}/${p.day}/${p.year} ${p.hour}:${p.minute}`;
};

const fmt = (v: unknown): string => {
  if (v == null || v === '') return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
};

const join = (a: unknown, b: unknown): string => [fmt(a), fmt(b)].filter(Boolean).join(', ');

type Highlight = 'green' | 'orange' | 'red';
interface Cell {
  label: string;
  value: string;
  highlight?: Highlight;
}

const HL: Record<Highlight, string> = {
  green: 'bg-green-600 text-white',
  orange: 'bg-amber-500 text-white',
  red: 'bg-red-600 text-white',
};

const Field: React.FC<Cell> = ({ label, value, highlight }) => (
  <div className="flex gap-1.5 py-0.5 text-[0.75rem]">
    <span className="min-w-[124px] shrink-0 text-right font-semibold text-slate-600 dark:text-gray-300">
      {label}:
    </span>
    <span
      className={
        highlight
          ? `px-1.5 rounded font-medium ${HL[highlight]}`
          : 'break-words text-slate-900 dark:text-gray-100'
      }
    >
      {value || ' '}
    </span>
  </div>
);

const Section: React.FC<{ title: string; cells: Cell[] }> = ({ title, cells }) => {
  // Match v1: render the whole section (all fields, blanks included) only when
  // at least one field carries a value; otherwise drop the section entirely.
  if (!cells.some((c) => c.value !== '' || c.highlight)) return null;
  return (
    <div>
      <div className="mb-1 text-[0.8125rem] font-bold text-slate-900 dark:text-gray-100">{title}</div>
      <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
        {cells.map((c, i) => (
          <Field key={i} {...c} />
        ))}
      </div>
    </div>
  );
};

export type ExceptionRow = {
  code?: string;
  severity?: string;
  message?: string;
  detected_at?: string;
};

/**
 * Severity row color, matching v1's `gwitraceShipmentHtml`: backred for
 * ERROR/CRITICAL/FATAL, backyellow for WARN, plain otherwise. The yellow and
 * red bands keep their own foreground in dark mode — they're signal colors,
 * not surface colors, so they shouldn't invert with the theme.
 */
const severityClass = (sev: string): string => {
  if (/ERROR|CRITICAL|FATAL/i.test(sev)) return 'bg-red-600 text-white';
  if (/WARN/i.test(sev)) return 'bg-amber-300 text-slate-900';
  return 'bg-slate-50 text-slate-800 dark:bg-gray-800 dark:text-gray-200';
};

/**
 * Active exceptions, rendered above the field table like v1 — they explain why
 * data may be missing, and (the reason this exists) they're where an LC reads
 * WHICH hold a container is sitting on to relay it to the customer.
 *
 * Caveat worth knowing when a hold seems missing: gwiTrace suppresses the
 * ACTIVE_HOLD row in two non-actionable windows, pre-arrival and post-pickup.
 * The raw per-category flags survive in the snapshot (terminal.holds /
 * rail.holds) and stay visible under Raw Results.
 */
// gwiTrace's two terminal badges: the export booking's and the import box's.
const TERMINAL_BADGES = new Set(['BOOKING_TERMINAL_MISMATCH', 'TERMINAL_MISMATCH']);

/**
 * The exceptions the popup shows for one trace result.
 *
 * When gwi360 has judged the terminal itself (`terminal_check`: POH's terminal
 * against the order's SAVED Terminal), gwiTrace's own terminal badge is dropped
 * and gwi360's line takes its place, or nothing when the two agree. gwiTrace's
 * badge compares POH with gwiTrace's REGISTRATION, which follows a save only
 * minutes later and is re-worded only at the booking's next sweep, and it
 * spoke gwiTrace's language ("POH works vessel visit NJU-GN644W at ... This
 * booking is registered at HOU-BCT", EBKG18964239, 2026-10-19).
 */
export const shownExceptions = (result: TraceResult): ExceptionRow[] => {
  const raw = (result.snapshot as { exceptions?: unknown } | null)?.exceptions;
  const rows: ExceptionRow[] = Array.isArray(raw) ? (raw as ExceptionRow[]) : [];
  const check = result.terminal_check;
  if (!check) return rows;
  const kept = rows.filter((e) => !TERMINAL_BADGES.has(String(e.code ?? '')));
  return check.message
    ? [{ code: check.code, severity: 'WARNING', message: check.message }, ...kept]
    : kept;
};

export const ExceptionsSection: React.FC<{ rows: ExceptionRow[] }> = ({ rows }) => {
  if (rows.length === 0) return null;
  return (
    <div className="mb-3">
      <div className="mb-1 text-[13px] font-bold text-slate-900 dark:text-gray-100">
        Exceptions
      </div>
      <div className="space-y-0.5">
        {rows.map((e, i) => (
          <div
            key={`${e.code ?? ''}-${i}`}
            className={`flex flex-wrap items-start gap-x-3 rounded px-2 py-1 text-[12px] ${severityClass(
              fmt(e.severity),
            )}`}
          >
            {/* Sized to the code, not a fixed 170px: BOOKING_TERMINAL_MISMATCH
                is wider than that and ran under its message (export popup,
                2026-10-15). The message wraps beneath on a narrow row. */}
            <span className="min-w-[170px] shrink-0 whitespace-nowrap">
              <span className="font-bold">{fmt(e.severity)}</span> {fmt(e.code)}
            </span>
            <span className="min-w-0 flex-1 break-words">{fmt(e.message)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const TraceSnapshotPanel: React.FC<{ result: TraceResult; showHeader?: boolean }> = ({
  result,
  showHeader = true,
}) => {
  const s = (result.snapshot ?? {}) as TraceSnapshot & Record<string, any>;
  const carriers = (s.carriers ?? {}) as Record<string, any>;
  const rail = (s.rail ?? {}) as Record<string, any>;
  const exceptions: ExceptionRow[] = Array.isArray(s.exceptions) ? s.exceptions : [];

  // Status + availability highlight (v1 logic): storage hold still counts as
  // available/green; available-but-past-LFD is orange.
  let statusText = fmt(s.container_status);
  let statusHi: Highlight | undefined = s.is_available_for_pickup ? 'green' : undefined;
  if (/storage hold/i.test(statusText)) {
    statusText = 'Available for Pickup';
    statusHi = 'green';
  }
  // #7940: a POH port-LFD customer's order reads the PORT date, not gwiTrace's
  // LINE date, so the popup shows the same. `??`, not `||`: '' is a real
  // answer (POH has no port date), never a cue to fall back to the line date.
  const lastFreeDay = result.port_lfd ?? s.last_free_day;
  const lfd = String(lastFreeDay ?? '');
  if (statusHi === 'green' && /^\d{4}-\d{2}-\d{2}$/.test(lfd)) {
    if (lfd < new Date().toISOString().slice(0, 10)) {
      statusText = 'Available (past LFD)';
      statusHi = 'orange';
    }
  }

  const shipment: Cell[] = [
    { label: 'Container', value: fmt(s.container_number) },
    { label: 'BOL', value: fmt(s.bill_of_lading) },
    { label: 'SSLine', value: fmt(carriers.steamship_line) },
    { label: 'Rail Carrier', value: fmt(carriers.rail) },
    { label: 'Chassis #', value: fmt(s.chassis_number) },
    { label: 'Status', value: statusText, highlight: statusText ? statusHi : undefined },
    { label: 'Mode', value: fmt(s.mode) },
    { label: 'Est Arrival', value: fmtDate(s.estimated_arrival_at) },
    { label: 'Final Dest', value: fmt(s.final_destination) },
    { label: 'Current Location', value: fmt(s.current_location) },
    { label: 'Available', value: fmtDate(s.available_at) },
    { label: 'Last Free Day', value: fmtDate(lastFreeDay) },
    { label: 'Outgate', value: fmtDate(s.gated_out_at) },
    { label: 'Empty Return', value: fmtDate(s.empty_returned_at) },
    { label: 'Customs Status', value: fmt(s.customs_status) },
    { label: 'Line Status', value: fmt(s.line_status) },
    { label: 'Customs Released', value: fmtDate(s.customs_released_at) },
    { label: 'Line Released', value: fmtDate(s.line_released_at) },
    { label: 'On Rail', value: fmt(s.is_on_rail) },
    // Fills the empty cell beside On Rail (19 rows in two columns), so no row
    // above moves. Decoded server-side; POH boxes only (LC request 2026-10-21).
    { label: 'Size / Type', value: fmt(result.size_type) },
  ];
  const ocean: Cell[] = [
    { label: 'Vessel', value: fmt(s.vessel_name) },
    { label: 'Voyage', value: fmt(s.voyage_number) },
    { label: 'Vessel Departed', value: fmtDate(s.vessel_departed_at) },
    { label: 'Vessel Arrived', value: fmtDate(s.vessel_arrived_at) },
    { label: 'Discharged', value: fmtDate(s.discharged_at) },
  ];
  const railCells: Cell[] = [
    { label: 'Origin Ramp', value: join(rail.origin_ramp, rail.origin_ramp_state) },
    { label: 'Destination Ramp', value: join(rail.destination_ramp, rail.destination_ramp_state) },
    { label: 'Train Depart', value: fmtDate(s.train_departed_at) },
    { label: 'Current Location', value: join(rail.rail_terminal, rail.rail_terminal_state) },
    { label: 'Grounded', value: fmtDate(s.grounded_at) },
    { label: 'Grounded Location', value: fmt(rail.location_at_terminal) },
  ];

  // EXPORT (POH booking tracking, live in prod 2026-10-12). An export snapshot
  // keeps its schedule under `export`; the ocean/rail fields above are IMPORT
  // rollups and stay blank on it. Without this block an export trace rendered
  // as a modal of dashes, which a CSR reads as "still not working" (PRO
  // 5144777, the day LYNX export was switched off).
  //
  // Two shapes, because a PRO's link can point at either row of a booking
  // (prod 2026-10-13: 2,305 placeholder links, 1,478 container links):
  //   placeholder - `export` is FLAT (the sweep writes the schedule there) and
  //                 nothing is promoted to the top level;
  //   container   - the schedule sits in the `export.terminal` DRAWER and the
  //                 rollup promotes vessel/cutoffs to the top level.
  // Read flat, then the drawer, then the promoted field.
  const exp = (s.export && typeof s.export === 'object' ? s.export : null) as Record<string, unknown> | null;
  const drawer = (exp?.terminal && typeof exp.terminal === 'object' ? exp.terminal : {}) as Record<string, unknown>;
  // The promoted top-level fallback is only trusted on a row whose export
  // section has content; an empty section is not evidence of an export row.
  const isExport = !!exp && Object.keys(exp).length > 0;
  const ex = (key: string): unknown => exp?.[key] ?? drawer[key] ?? (isExport ? s[key] : undefined);
  const exportCells: Cell[] = exp
    ? [
        { label: 'Booking', value: fmt(ex('booking_number')) },
        { label: 'Terminal', value: fmt(ex('origin_terminal')) },
        { label: 'SSLine', value: fmt(ex('line_scac')) },
        { label: 'Vessel', value: fmt(ex('vessel_name')) },
        { label: 'Voyage', value: fmt(ex('voyage_number')) },
        { label: 'Visit Phase', value: fmt(ex('visit_phase')) },
        { label: 'First Receive (ERD)', value: fmtDate(ex('early_return_date')) },
        { label: 'Cargo Cutoff', value: fmtDate(ex('cargo_cutoff')) },
        { label: 'HAZ Cutoff', value: fmtDate(ex('haz_cutoff')) },
        { label: 'Reefer Cutoff', value: fmtDate(ex('reefer_cutoff')) },
        // The port's SI deadline. NOT the CMS doc cutoff, which the bridge
        // derives from the cargo cutoff (previous work day) - labelling this
        // "Doc Cutoff" would make the two look like a disagreement.
        { label: 'SI Cutoff', value: fmtDate(ex('si_cutoff')) },
        { label: 'ETD', value: fmtDate(ex('etd')) },
        { label: 'Vessel ETA', value: fmtDate(ex('vessel_eta') ?? s.estimated_arrival_at) },
        { label: 'POL / POD', value: join(ex('pol_locode'), ex('pod_locode')) },
      ]
    : [];
  // Swap the import groups out only when the export side has something to
  // say; an export row with an empty section still shows whatever it has.
  const hasExport = exportCells.some((c) => c.value !== '');
  // An export shows only export fields: the Shipment block keeps the three
  // rows that mean something on a booking and drops the import-only rows
  // (chassis, status, availability, LFD, out-gate, holds, rail), which were
  // printing blank on every export (user, 2026-10-13).
  const exportShipment: Cell[] = [
    { label: 'Container', value: fmt(s.container_number) },
    { label: 'BOL', value: fmt(s.bill_of_lading) },
    { label: 'Mode', value: fmt(s.mode) },
  ];
  // An export placeholder the port has NOTHING on yet (BOOKING_NOT_FOUND):
  // no container, and the port's verdict on the booking. Before this it
  // fell through to the import layout and printed every import row blank
  // under the warning, which a CSR read as a second problem (prod
  // 038VM1574681, 2026-10-14). Show the warning and the booking, nothing
  // else.
  //
  // Keyed on the BADGE, not on the export section: a placeholder minted by
  // registration when the port said "no such booking" carries no `export`
  // key at all (55 of the 90 not-found placeholders in prod, 2026-10-14),
  // so the first cut, which required the key, never fired on the booking
  // it was written for.
  const notFound = exceptions.some((e) => e.code === 'BOOKING_NOT_FOUND');
  const isExportPlaceholder = (exp !== null || notFound) && !String(s.container_number ?? '').trim();
  const bare = !hasExport && isExportPlaceholder;
  const bareShipment: Cell[] = [
    { label: 'Booking', value: fmt(s.booking_number ?? s.bill_of_lading) },
  ];

  const providers = (s.providers ?? {}) as Record<string, any>;
  const providerNames = Object.keys(providers).sort();

  return (
    <div className="rounded border border-slate-200 bg-white p-3 text-left dark:border-gray-700 dark:bg-gray-900">
      {showHeader && (
        <div className="mb-1 text-center text-[0.6875rem] text-slate-500 dark:text-gray-400">
          Shipment ID: {result.shipment_id ?? '—'}
          {result.last_synced_at ? `  |  Last Synced: ${fmtDate(result.last_synced_at)}` : ''}
        </div>
      )}

      {/* Exceptions first, like v1 — they explain why fields below may be blank. */}
      <ExceptionsSection rows={shownExceptions(result)} />

      <div className="space-y-3">
        <Section
          title="Shipment"
          cells={bare ? bareShipment : hasExport ? exportShipment : shipment}
        />
        {hasExport ? (
          <Section title="Export Booking" cells={exportCells} />
        ) : bare ? null : (
          <>
            <Section title="Ocean (SSLine)" cells={ocean} />
            <Section title="Rail" cells={railCells} />
          </>
        )}
      </div>

      {providerNames.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-[0.8125rem] font-bold text-slate-900 dark:text-gray-100">Providers</div>
          {providerNames.map((name) => {
            const p = (providers[name] ?? {}) as Record<string, any>;
            const hl = p.last_error ? HL.red : p.is_receiving_data ? HL.green : '';
            return (
              <div key={name} className="flex flex-wrap items-center gap-x-4 gap-y-0.5 py-0.5 text-[0.75rem]">
                <span className={`px-1.5 rounded font-semibold ${hl}`}>
                  {name} ({fmt(p.provider_type)})
                </span>
                <span className="text-slate-700 dark:text-gray-300">
                  Last success: {fmtDate(p.last_success_at) || '—'}
                </span>
                <span className="text-slate-700 dark:text-gray-300">
                  Receiving: {p.is_receiving_data ? 'Yes' : 'No'}
                </span>
                {p.last_error ? (
                  <span className="text-red-600 dark:text-red-400">{fmt(p.last_error)}</span>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <details className="mt-3">
        <summary className="cursor-pointer text-[0.75rem] font-semibold text-blue-600 dark:text-blue-400">
          Raw Results
        </summary>
        <pre className="mt-1 max-h-80 overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-2 text-[0.6875rem] text-slate-800 dark:bg-gray-800 dark:text-gray-200">
          {JSON.stringify(result.snapshot, null, 2)}
        </pre>
      </details>
    </div>
  );
};

export default TraceSnapshotPanel;
